import { Manufacturer } from "../../../../core/models/entities/Manufacturer";
import { useEffect, useState } from "react";
import { useCustomerActionHandler } from "../hooks/useCustomerActionHandler";
import { Organization } from "../../../../core/models/entities/Organization";
import { CustomerCreateForm_VM } from "../components/customer-create-form.component";
import { isCustomerFormInvalid } from "../functions/is-customer-form-valid.function";
import { toast } from "react-toastify";
import { useErrorMessage } from "../../../hooks/useErrorMessage";
import { useProfileContext } from "../../../../contexts/ProfileContext";
import { Role } from "../../../../core/models/entities/Role";
import { CustomerForm } from "../components/customer.form";
import { LogoOptions } from "../../../../core/models/entities/LogoOptions";
import {
  Commission,
  CommissionProfile,
} from "../../../../core/models/entities/Commission";
import { useRepositoriesContext } from "../../../../contexts/RepositoriesContext";

export function useCustomerCreateFormViewModel(
  manufacturers: Manufacturer[],
  vendors: Organization[] | undefined,
  logoOptions: LogoOptions
): CustomerCreateForm_VM {
  const { organizationRepository } = useRepositoriesContext();
  const { profile, role } = useProfileContext();
  const { data: vendor } = organizationRepository.useVendor(
    profile?.userOrganizationInformations?.organizationId
  );
  const [form, setForm] = useState<CustomerForm>({
    name: "",
    contactEmail: "",
    email: "",
    contactPerson: "",
    contactPhone: "",
    phone: "",
    address: "",
    cvr: "",
    excludedManufacturers: [] as Manufacturer[],
    parentId: undefined,
    eori: "",
    shippingName: "",
    shippingAddress: "",
    shippingPostCode: "",
    shippingCity: "",
    shippingCountry: "",
    shippingContactPerson: "",
    shippingContactPhone: "",
    shippingContactEmail: "",
    vatRate: vendor?.defaultVatRate,
  } as CustomerForm);
  const [commissionProfile, setCommissionProfile] =
    useState<CommissionProfile>();
  const [vendorCommissionProfile, setVendorCommissionProfile] =
    useState<CommissionProfile>();
  const { handleGoToCustomerList, handleCreate, isLoading } =
    useCustomerActionHandler();
  const [commissionLoading, setCommissionLoading] = useState(false);
  const { tError } = useErrorMessage();

  useEffect(() => {
    if (vendor) {
      setForm({ ...form, vatRate: form.vatRate ?? vendor.defaultVatRate });
      // Set vendor's commission as default for new customers
      if (vendor.commission && !commissionProfile) {
        setVendorCommissionProfile(vendor.commission);
        setCommissionProfile(vendor.commission);
      }
    }
  }, [vendor]);

  function handleCreateClick(): Promise<void> {
    const formValid = !isCustomerFormInvalid(form);
    if (!formValid) {
      toast.error("Form is invalid");
      return Promise.reject();
    }

    if (formValid && !form.parentId && role === Role.Admin) {
      toast.error("Admins must choose a Vendor");
      return Promise.reject();
    }

    if (formValid && form.parentId && role !== Role.Admin) {
      toast.error("Illegal action");
      return Promise.reject();
    }

    const s = {
      ...form,
      isVendor: false,
      parentId:
        form.parentId ?? profile!.userOrganizationInformations.organizationId,
      id: 0,
      excludedManufacturers: form.excludedManufacturers?.map((x) => x.id),
      commission: undefined,
    } as Organization;

    async function transaction() {
      const org = await handleCreate(s);
      if (commissionProfile) {
        setCommissionLoading(true);
        const result = await organizationRepository.setCommissionProfile(
          org.id,
          commissionProfile
        );
        setCommissionLoading(false);
        return result;
      }
    }

    return transaction()
      .then(() => {
        toast.success("Customer is Created");
        handleGoToCustomerList();
      })
      .catch((e) => Promise.reject(tError(e)));
  }

  function updateCommission(commission: CommissionProfile): void {
    setCommissionProfile(commission);
  }

  function handleCancel() {
    handleGoToCustomerList();
  }

  return {
    onCreate: handleCreateClick,
    onCancel: handleCancel,
    form: form,
    setForm: setForm,
    manufactures: manufacturers,
    isFormValid: !isCustomerFormInvalid(form),
    isLoading: isLoading || commissionLoading,
    showVendorPicker: profile?.userOrganizationInformations.role === Role.Admin,
    vendors: vendors,
    selectedVendor: vendors
      ? vendors.find((x) => x.id === form.parentId)
      : undefined,
    logoOptions: logoOptions,
    updateCommission: updateCommission,
    commissionProfile:
      commissionProfile ??
      vendorCommissionProfile ??
      ({
        typeOnly: [] as Commission[],
        methods: [] as Commission[],
      } as CommissionProfile),
  };
}
